//////////////////
// IMPORTS
//////////////////
// schema
import * as ISchema from "@schema";
// libraries
import * as React from "react";
import { createFragmentContainer } from "react-relay";
import { connect } from "react-redux";
// eslint-disable-next-line @typescript-eslint/no-var-requires, @typescript-eslint/no-unsafe-assignment
const ReactHighcharts = require("react-highcharts");
// selection-scheduler imports
import { IProps, IContainer, IState } from "./ProfileTimeVisualizations.d";
import * as s from "./ProfileTimeVisualizations.styles";
// other components
import { H2 } from "@ui";
import { TimeAllocation } from "@components/time-allocation";
// constants
import { daysOfWeek, dayToIndex } from "@constants/date.constant";
// assets
import { brokenClock } from "@img/illustrations";
// state
import { IReduxState } from "@reducers/root.reducer";

//////////////////
// SETUP
//////////////////
const labels = {
  FOCUS_TIME: "Focus Time",
  MEETINGS: "meetings",
  OTHER: "other events",
  FRAGMENTED_TIME: "fragmented time",
};

//////////////////
// COMPONENT
//////////////////
class ProfileTimeVisualizationsComponent extends React.Component<IProps, IState> {
  constructor(props: IProps) {
    super(props);
  }

  private getRoundedHours = (minutes: number) => {
    if (minutes > 600) {
      // if over 10, round to nearest integer
      return Math.round(minutes / 60);
    } else {
      // otherwise round to the nearest .5
      return Math.round(minutes / 30) / 2;
    }
  };

  // ~-~-~-~-~-~-~-
  // Render
  // ~-~-~-~-~-~-~-

  private renderTimeOverWeek = (
    calendarTimeStats: ISchema.ICalendarTimeStats,
    userWorkingHours: ISchema.IUserWorkingHours,
  ) => {
    const { scoresByDay } = calendarTimeStats;
    const {
      workingHours: { daySettings },
    } = userWorkingHours;

    // Default to regular work week.
    let graphStartDay = ISchema.Day.Monday;
    let graphEndDay = ISchema.Day.Friday;

    // Only expand graph if user's working hours have Sunday or Saturday.
    for (const daySetting of daySettings) {
      if (daySetting.day === ISchema.Day.Sunday) {
        graphStartDay = ISchema.Day.Sunday;
      } else if (daySetting.day === ISchema.Day.Saturday) {
        graphEndDay = ISchema.Day.Saturday;
      }
    }

    const relevantDays = daysOfWeek.slice(dayToIndex[graphStartDay], dayToIndex[graphEndDay] + 1);

    const focusMinutesByDay = relevantDays.map((day) => {
      const dayScore = scoresByDay.find((score) => score.secondaryKey === day);
      return (dayScore && this.getRoundedHours(dayScore.focusMinutes)) || 0;
    });

    const meetingsByDay = relevantDays.map((day) => {
      const dayScore = scoresByDay.find((score) => score.secondaryKey === day);
      return (dayScore && this.getRoundedHours(dayScore.busyMinutes)) || 0;
    });

    const config = {
      series: [
        {
          color: s.focusColor,
          name: labels.FOCUS_TIME,
          data: focusMinutesByDay,
        },
        {
          color: s.busyColor,
          name: labels.MEETINGS,
          data: meetingsByDay,
        },
      ],
      chart: {
        height: s.chart.chart.height,
      },
      credits: {
        enabled: false,
      },
      tooltip: {
        headerFormat: "<b>{point.x}</b><br/>",
        pointFormat: "{series.name}: {point.y} hr",
      },
      title: {
        text: "",
        marginTop: s.chart.title.marginTop,
      },
      xAxis: {
        categories: relevantDays,
        tickColor: "transparent",
        gridLineColor: "transparent",
        labels: {
          formatter: function (): string {
            return (this as any).value.substring(0, 3);
          },
        },
      },
      yAxis: {
        title: {
          text: null,
        },
        labels: {
          enabled: false,
        },
        min: 0,
        max: Math.max(...focusMinutesByDay, ...meetingsByDay) || 12,
        gridLineColor: s.chart.yAxis.gridLineColor,
      },
    };

    return <ReactHighcharts config={config} />;
  };

  private renderContent = (
    calendarTimeStats: ISchema.ICalendarTimeStats,
    userWorkingHours: ISchema.IUserWorkingHours,
  ) => {
    const {
      focusMinutes,
      busyMinutes,
      fragmentedMinutes,
      unclassifiedMinutes,
      totalMinutes,
    } = calendarTimeStats;

    return (
      <React.Fragment>
        <TimeAllocation
          focusMinutes={focusMinutes}
          busyMinutes={busyMinutes}
          fragmentedMinutes={fragmentedMinutes}
          unclassifiedMinutes={unclassifiedMinutes}
          totalMinutes={totalMinutes}
        />
        {this.renderTimeOverWeek(calendarTimeStats, userWorkingHours)}
      </React.Fragment>
    );
  };

  private maybeRenderError = (calendarTimeStats: ISchema.ICalendarTimeStats) => {
    if (
      calendarTimeStats.totalMinutes === null ||
      calendarTimeStats.focusMinutes === null ||
      calendarTimeStats.busyMinutes === null ||
      calendarTimeStats.fragmentedMinutes === null ||
      calendarTimeStats.unclassifiedMinutes === null
    ) {
      return (
        <div
          style={{
            position: "absolute",
            top: 0,
            bottom: 0,
            textAlign: "center",
            background: "#fff",
          }}
        >
          <img style={{ width: 200 }} src={brokenClock} alt="broken clock" />
          <div style={{ marginTop: 15 }}>
            We couldn't load analytics. Please try again in a few minutes.
          </div>
        </div>
      );
    }

    return "";
  };

  public render() {
    const { org } = this.props;
    return (
      <div>
        <H2 style={s.title}>Time allocation</H2>
        <div style={{ position: "relative" }}>
          {this.renderContent(org.calendarTimeStats, org.userWorkingHours)}
          {this.maybeRenderError(org.calendarTimeStats)}
        </div>
      </div>
    );
  }
}

//////////////////
// REDUX
//////////////////
function mapStateToProps(_state: IReduxState, _ownProps: IContainer) {
  return {};
}

const reduxContainer = connect(mapStateToProps, {})(ProfileTimeVisualizationsComponent);

//////////////////
// RELAY
//////////////////
import { fragments } from "./ProfileTimeVisualizations.gql";
export const ProfileTimeVisualizations = createFragmentContainer<IContainer>(
  reduxContainer,
  fragments,
);
